/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey
 */

import { Observable, Subject, Subscription } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { Injectable, OnDestroy } from '@angular/core';
import { AddParams, ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { ApiSortModel } from '@app/models/api-sort.model';
import { Logger } from '../logger/logger.service';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
import { SubSink } from '@base/node_modules/subsink/dist/subsink';
import { ProjectModel } from '@app/models/project.model';
@Injectable({
  providedIn: 'root',
})
export class ApiBaseService implements OnDestroy {
  protected delaySubscription: Subscription;
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  protected subscriptionContainer = new SubSink();

  public dataSubject = new Subject<any>();
  public insertSubject = new Subject<any>();
  public updateSubject = new Subject<any>();
  public deleteSubject = new Subject<any>();
  private _protocol: string = environment.serverProtocol;
  private _address: string = environment.testing
    ? environment.testApiUrl
    : `${environment.secureServerUrl}`;
  protected _endpoint = '';
  public log = new Logger('ApiBaseService');
  public fetchTimestamp: Date;
  public set protocol(protocol: string) {
    this._protocol = protocol;
  }

  public set address(address: string) {
    this._address = address;
  }

  public set endpoint(endpoint: string) {
    this._endpoint = endpoint;
  }

  constructor(protected http: HttpClient, protected authUserService: AuthUserService) {}

  private get baseUrl(): string {
    return `${this._protocol}://${this._address}`;
  }

  private get options(): any {
    // Updated to user HttpHeaders as old config was not pulling through correctly
    let newHeaders: HttpHeaders | Headers;
    if (environment.testing) {
      newHeaders = new Headers({ endpoint: this._endpoint });
      newHeaders.append('Authorization', this.authUserService.credentials.idToken);
      newHeaders.append('Content-Type', 'application/json');
      newHeaders.append('Prefer', 'return=representation');
      newHeaders.append('Accept', 'application/json');
      newHeaders.append('clientId', environment.cognitoSettings.clientId);
    } else {
      newHeaders = new HttpHeaders({ endpoint: this._endpoint });
      if (this.authUserService.credentials && this.authUserService.credentials.idToken) {
        newHeaders = newHeaders
          .append('Authorization', this.authUserService.credentials.idToken)
          .append('clientId', environment.cognitoSettings.clientId);
      }
    }

    return {
      headers: newHeaders,
      withCredentials: true,
    };
  }

  /**
   *  Warren Dowey
   *
   *  processQuery
   *
   *  process any API calls with a postgrest query, by ingesting the input and querying the API in the URL
   *
   * @param filter of type ApiFilterModel containing {field: string; operator: string; value: any;}
   * @param select of type array of strings of how to select the data
   * @param order of type ApiSortModel containing {filed: string; direction: string}
   * @param limit number to limit to
   * @param add
   * @todo TODO: needs a lot of working out around && and ||s
   *
   * @returns querystring of type string; after the query has been processed
   */
  protected processQuery(
    filter?: ApiFilterModel[],
    select?: string[],
    order?: ApiSortModel[],
    limit?: number[],
    add?: AddParams
  ) {
    let queryString = '';
    const filterArray = [];
    const orderArray = [];
    const limitArray = [];
    const addArray = [];
    let appendSymbol = '?';
    if (filter !== undefined) {
      for (const f of filter) {
        if (f.joinOperator) {
          let x = 0;
          let tempFilter = f.joinOperator.toLowerCase() + '=(';
          for (const operator of f.operator) {
            if (x > 0) {
              tempFilter += ',';
            }
            switch (operator) {
              case 'eq':
              case 'gte':
              case 'gt':
              case 'lt':
              case 'lte':
              case 'neq':
              case 'is':
              case 'like':
              case 'ilike':
                tempFilter += f.field[x] + '.' + operator + '.' + f.value[x];
                break;
              case 'in':
                tempFilter += f.field[x] + '.' + operator + '.(' + f.value[x] + ')';
                break;
              case 'fts':
                tempFilter += f.field[x] + '.' + operator + '.' + f.value[x];
                break;
              // TODO: work out how these work
              case '@@':
                tempFilter += f.field[x] + '.' + operator + '.' + f.value[x];
                break;
              // TODO: work out how these work
              case '@>':
                tempFilter += f.field[x] + '.' + operator + '.{' + f.value[x] + '}';
                break;
              // TODO: work out how these work
              case '<@':
                tempFilter += f.field[x] + '.' + operator + '{' + f.value[x] + '}';
                break;

              default:
                this.log.debug('filter added but no matching operator');
            }
            x++;
          }
          tempFilter += ')';
          filterArray.push(tempFilter);
        } else {
          switch (f.operator) {
            case 'eq':
            case 'gte':
            case 'gt':
            case 'lt':
            case 'lte':
            case 'neq':
            case 'is':
            case 'like':
            case 'ilike':
              filterArray.push(f.field + '=' + f.operator + '.' + f.value);
              break;
            case 'in':
              filterArray.push(f.field + '=' + f.operator + '.(' + f.value + ')');
              break;
            // TODO: work out how these work
            case '@@':
              filterArray.push(f.field + '=' + f.operator + '.' + f.value);
              break;
            // TODO: work out how these work
            case '@>':
              filterArray.push(f.field + '=' + f.operator + '.{' + f.value + '}');
              break;
            // TODO: work out how these work
            case '<@':
              filterArray.push(f.field + '=' + f.operator + '{' + f.value + '}');
              break;
            default:
              this.log.debug('filter added but no matching operator');
          }
        }
      }
    }
    if (order !== undefined) {
      for (const o of order) {
        orderArray.push(o.field + '.' + o.direction);
      }
    }

    if (limit) {
      for (const l of limit) {
        limitArray.push(l);
      }
    }

    if (filterArray.length) {
      queryString = queryString + appendSymbol + filterArray.join('&');
      appendSymbol = '&';
    }

    if (orderArray.length) {
      queryString = queryString + appendSymbol + 'order=' + orderArray.join(',');
      appendSymbol = '&';
    }

    if (limitArray.length) {
      queryString = queryString + appendSymbol + 'limit=' + limitArray.join(',');
    }

    if (select !== undefined && select.length) {
      queryString = queryString + appendSymbol + 'select=' + select.join(',');
    }

    if (add !== undefined) {
      queryString = queryString + appendSymbol + add.field + '=eq.' + add.value;
    }
    return queryString;
  }

  /**
   *  Warren Dowey
   *
   *  getEndpoint
   *
   *  get the endpoint of an API call by processing the query received and adding this to the postgrest URL.
   * Following this call a get request from HTTP to get the contents of the URL.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   *
   * @returns Observable<ArrayBuffer> : get the data from an API query as an observable
   */
  public getEndpoint(params: ApiParamsModel): Observable<ArrayBuffer> {
    const endpointUrl = this.getEndpointUrl(params);
    return this.http.get(endpointUrl, this.options);
  }

  /**
   *  Warren Dowey
   *
   *  postEndpoint
   *
   *  get the endpoint of an API call by processing the query received and adding this to the postgrest URL.
   * Following this call a post request from HTTP to post the new data to the contents of the URL.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param data of type any containing that is to be added to the data at the endpoint
   *
   * @returns Observable<ArrayBuffer> : post the data at the endpoint of an API query as an observable
   */
  public postEndpoint(params: ApiParamsModel, data: any): Observable<ArrayBuffer> {
    const endpointUrl = this.getEndpointUrl(params);
    return this.http.post(endpointUrl, data, this.options);
  }
  /**
   *  Warren Dowey
   *
   *  patchEndpoint
   *
   *  get the endpoint of an API call by processing the query received and adding this to the postgrest URL.
   * Following this call a patch request from HTTP to patch the new data onto the contents of the URL.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param data of type any containing that is to be patched to the data at the endpoint
   * @param oldData of type any of the details of the data that existed before the patch occurs
   *
   * @returns Observable<ArrayBuffer> : patch the data at the endpoint of an API query as an observable
   */
  public patchEndpoint(params: ApiParamsModel, data: any, oldData: any): Observable<ArrayBuffer> {
    const endpointUrl = this.getEndpointUrl(params);
    // Set new headers so that the oldData can be passed in, in order to populate the history_events table
    let newHeaders: HttpHeaders | Headers;
    if (environment.testing) {
      newHeaders = new Headers({
        endpoint: this._endpoint,
        data: oldData,
      });
      newHeaders.append('Authorization', this.authUserService.credentials.idToken);
      newHeaders.append('Content-Type', 'application/json');
      newHeaders.append('Prefer', 'return=representation');
      newHeaders.append('Accept', 'application/json');
      newHeaders.append('clientId', environment.cognitoSettings.clientId);
    } else {
      newHeaders = new HttpHeaders({
        endpoint: this._endpoint,
        data: oldData,
      });
      if (this.authUserService.credentials && this.authUserService.credentials.idToken) {
        newHeaders = newHeaders
          .append('Authorization', this.authUserService.credentials.idToken)
          .append('clientId', environment.cognitoSettings.clientId);
      }
    }
    const patchOptions: any = {
      headers: newHeaders,
      withCredentials: true,
    };
    return this.http.patch(endpointUrl, data, patchOptions);
  }

  /**
   *  Warren Dowey
   *
   *  deleteEndpoint
   *
   *  get the endpoint of an API call by processing the query received and adding this to the postgrest URL.
   * Following this call a delete request from HTTP to delete the data from the contents of the URL.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param data
   * @param oldData
   *
   * @returns Observable<ArrayBuffer> : deleted the data at the endpoint of an API query as an observable
   */
  public deleteEndpoint(params: ApiParamsModel, data: any, oldData: any): Observable<ArrayBuffer> {
    const endpointUrl = this.getEndpointUrl(params);
    // Set newheaders so that the oldData can be passed in, in order to populate the history_events table
    // Set new headers so that the oldData can be passed in, in order to populate the history_events table
    let newHeaders: HttpHeaders | Headers;
    if (environment.testing) {
      newHeaders = new Headers({
        endpoint: this._endpoint,
        data: oldData,
      });
      newHeaders.append('Authorization', this.authUserService.credentials.idToken);
      newHeaders.append('Content-Type', 'application/json');
      newHeaders.append('Prefer', 'return=representation');
      newHeaders.append('Accept', 'application/json');
      newHeaders.append('clientId', environment.cognitoSettings.clientId);
    } else {
      newHeaders = new HttpHeaders({
        endpoint: this._endpoint,
        data: oldData,
      });
      if (this.authUserService.credentials && this.authUserService.credentials.idToken) {
        newHeaders = newHeaders
          .append('Authorization', this.authUserService.credentials.idToken)
          .append('clientId', environment.cognitoSettings.clientId);
      }
    }
    // Due to different syntax pass the data alongside the object for data inserted
    const deleteOptions: any = {
      headers: newHeaders,
      data: data,
      body: params,
      withCredentials: true,
    };
    return this.http.delete(endpointUrl, deleteOptions);
  }
  /**
   *  Warren Dowey
   *
   *  getData
   *
   *  call a HTTP request to get an observable of the data from the endpoint and subscribe to this,
   * passing it through a subject or handling errors using the HandleData or the HandleError functions.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   *
   * @returns Void: subscribes to the get http request so we can handle the data.
   */
  public getData(params: ApiParamsModel) {
    this.subscriptionContainer.sink = this.getEndpoint(params).subscribe(
      (data) => {
        this.handleData(data);
      },
      (error) => this.handleError('get', error)
    );
  }

  /**
   *  Warren Dowey
   *
   *  insertData
   *
   *  call a HTTP request to post to an observable of the data from the endpoint and subscribe to this,
   * passing it through a subject or handling errors using the handleInsert or the HandleError functions.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param dataToBeInserted of type any to be inserted into the data
   *
   * @returns Void: subscribes to the get post http request so we can handle the data.
   */
  public insertData(params: ApiParamsModel, dataToBeInserted: any) {
    // Insert the new data inserted into the database
    this.subscriptionContainer.sink = this.postEndpoint(params, dataToBeInserted).subscribe(
      (data) => {
        let update: any;
        if (Array.isArray(dataToBeInserted)) {
          update = dataToBeInserted;
        } else {
          update = [dataToBeInserted];
        }
        // Handle the newly inserted data
        this.handleInsert(update);
      },
      (error) => this.handleError('insert', error)
    );
  }
  /**
   *  Warren Dowey
   *
   *  updateData
   *
   *  call a HTTP request to patch to an observable of the data from the endpoint and subscribe to this,
   * passing it through a subject or handling errors using the handleUpdate or the HandleError functions.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param dataUpdated of type any to be updated into the data
   *
   * @returns Void: subscribes to the get patch http request so we can handle the data.
   */
  public updateData(params: ApiParamsModel, dataUpdated: any) {
    // Get the current data - before the data is updated
    // This is to be used as 'Old Data' so that it can be passed in the header for the history_actions table
    this.subscriptionContainer.sink = this.getEndpoint(params).subscribe(
      (oldData) => {
        // Pass in the oldData to the patchEndpoint so it can be passed into the headers above
        this.subscriptionContainer.sink = this.patchEndpoint(
          params,
          dataUpdated,
          oldData
        ).subscribe(() => {
          let update: any;
          if (Array.isArray(dataUpdated)) {
            update = dataUpdated;
          } else {
            update = [dataUpdated];
          }
          this.handleUpdate(update);
        });
      },
      (error) => this.handleError('update', error)
    );
  }
  /**
   *  Warren Dowey
   *
   *  deleteData
   *
   *  call a HTTP request to delete from an observable of the data from the endpoint and subscribe to this,
   * passing it through a subject or handling errors using the handleDelete or the HandleError functions.
   *
   * @param params of type ApiParamsModel containing {field: string; operator: string; value: any;}
   * @param dataToBeRemoved of type any to be delete from the data
   *
   * @returns Void: subscribes to the get delete http request so we can handle the data.
   */
  public deleteData(params: ApiParamsModel, dataToBeRemoved: any) {
    // Get the current data - before the data is updated
    // This is to be used as 'Old Data' so that it can be passed in the header for the history_actions table
    this.subscriptionContainer.sink = this.getEndpoint(params).subscribe((oldData) => {
      // Pass in the oldData to the deleteEndpoint so it can be passed into the headers above
      this.subscriptionContainer.sink = this.deleteEndpoint(
        params,
        dataToBeRemoved,
        oldData
      ).subscribe(
        () => {
          // Handle the newly data to be deleted
          this.handleDelete(oldData);
        },
        (error) => this.handleError('delete', error)
      );
    });
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   *  passes data from the get API request through a subject so it can be handled from multiple sources
   *
   * @param data of type any to get from the API
   *
   * @returns Void: calls next on datasubject with the API get request data
   */
  protected handleData(data: any) {
    this.dataSubject.next(data);
  }

  /**
   *  Warren Dowey
   *
   *  handleInsert
   *
   *  passes data from the post API request through a subject so it can be handled from multiple sources
   *
   * @param data of type any to post to the API
   *
   * @returns Void: calls next on datasubject with the API post request data
   */
  protected handleInsert(data: any) {
    this.insertSubject.next(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleUpdate
   *
   *  passes data from the patch API request through a subject so it can be handled from multiple sources
   *
   * @param data of type any to patch to the API
   *
   * @returns Void: calls next on datasubject with the API patch request data
   */
  protected handleUpdate(data: any) {
    this.updateSubject.next(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleDelete
   *
   *  passes data from the delete API request through a subject so it can be handled from multiple sources
   *
   * @param data of type any to delete to the API
   *
   * @returns Void: calls next on datasubject with the API delete request data
   */
  protected handleDelete(data: any) {
    this.deleteSubject.next(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleError
   *
   *  passes errors from any API request through a subject so it can be handled from multiple sources
   *
   * @param error of type any to be processed
   *
   * @returns Void: calls next on errorsubject with the API error
   */
  protected handleError(type: string, error: ErrorEvent) {
    switch (type) {
      case 'get':
        this.dataSubject.error(error);
        break;
      case 'update':
        this.updateSubject.error(error);
        break;
      case 'delete':
        this.deleteSubject.error(error);
        break;
      case 'insert':
        this.insertSubject.error(error);
        break;
    }
    throw error;
  }

  private getEndpointUrl(params: ApiParamsModel) {
    return `${this.baseUrl}/${this._endpoint}${this.processQuery(
      params.filter,
      params.select,
      params.sort,
      params.limit,
      params.add
    )}`;
  }

  ngOnDestroy(): void {
    this.subscriptionContainer.unsubscribe();
  }
}
